/**
 * StateBlock — the one way a list, panel or page says "nothing to show yet" (hand-off README rule 3).
 * Six kinds: empty, loading, error, forbidden, subscription, filtered. Design:
 * designs/app/StateBlock.dc.html (copy and defaults are the design's).
 *
 * Error rules (COMPONENTS.md "Error and permission mapping"): show the server's `message`; offer
 * "Try again" ONLY when `retryable`; otherwise link the docs or support; show `requestId` as a
 * reference a person can quote.
 */
import type { ReactNode } from 'react';
import { CreditCard, Inbox, Lock, SearchX, TriangleAlert, type LucideIcon } from 'lucide-react';
import { Button } from '../primitives/button';
import { cn } from '../utils';

export type StateBlockKind = 'empty' | 'loading' | 'error' | 'forbidden' | 'subscription' | 'filtered';

/** A button in the block: a handler, or a link. */
export interface StateBlockAction {
  readonly label: string;
  readonly onClick?: (() => void) | undefined;
  readonly href?: string | undefined;
}

export interface StateBlockProps {
  readonly kind: StateBlockKind;
  readonly title?: string | undefined;
  readonly message?: string | undefined;
  /** error only: the server said trying again could work. Shows "Try again" (needs onRetry). */
  readonly retryable?: boolean | undefined;
  readonly onRetry?: (() => void) | undefined;
  /** error only: shown as "Reference …" so support can find the request. */
  readonly requestId?: string | undefined;
  /** error only, when not retryable: the server's docs link for this error. */
  readonly docsUrl?: string | undefined;
  readonly primary?: StateBlockAction | undefined;
  readonly secondary?: StateBlockAction | undefined;
  /** loading only: how many skeleton rows. Default 4. */
  readonly rows?: number | undefined;
  readonly className?: string | undefined;
}

interface KindDefaults {
  readonly icon: LucideIcon;
  readonly tone: 'neutral' | 'danger' | 'warning';
  readonly title: string;
  readonly message: string;
}

const KINDS: Record<Exclude<StateBlockKind, 'loading'>, KindDefaults> = {
  empty: { icon: Inbox, tone: 'neutral', title: 'Nothing here yet', message: 'When there is something to show, it will appear here.' },
  error: { icon: TriangleAlert, tone: 'danger', title: 'This didn’t load', message: 'Something went wrong on our side.' },
  forbidden: { icon: Lock, tone: 'neutral', title: 'You don’t have access to this', message: 'Your role doesn’t include this. A workspace admin can give you access.' },
  subscription: { icon: CreditCard, tone: 'warning', title: 'Your subscription needs attention', message: 'This is paused until billing is sorted. Your data is safe and nothing has been deleted.' },
  filtered: { icon: SearchX, tone: 'neutral', title: 'No matches', message: 'Nothing matches these filters.' },
};

/** Skeleton bar widths from the design, cycled. */
const SKELETON_WIDTHS = ['38%', '52%', '30%', '46%'];

function ActionButton({ action, variant }: { action: StateBlockAction; variant: 'default' | 'ghost' }) {
  if (action.href) {
    return (
      <Button asChild size="sm" variant={variant}>
        <a href={action.href} target={action.href.startsWith('http') ? '_blank' : undefined} rel="noreferrer">{action.label}</a>
      </Button>
    );
  }
  return <Button size="sm" variant={variant} onClick={action.onClick}>{action.label}</Button>;
}

export function StateBlock(props: StateBlockProps) {
  const { kind, className } = props;

  if (kind === 'loading') {
    const rows = Math.max(1, props.rows ?? 4);
    return (
      <div className={cn('ifx-state ifx-state--loading', className)} role="status" aria-busy="true" aria-live="polite">
        {Array.from({ length: rows }, (_, i) => (
          <div className="ifx-state__skeleton-row" key={i} aria-hidden="true">
            <span className="ifx-state__skeleton-icon" />
            <span className="ifx-state__skeleton-bar" style={{ width: SKELETON_WIDTHS[i % SKELETON_WIDTHS.length] }} />
            <span className="ifx-state__skeleton-spacer" />
            <span className="ifx-state__skeleton-end" />
          </div>
        ))}
        <span className="ifx-state__loading-text">{props.message ?? 'Loading…'}</span>
      </div>
    );
  }

  const d = KINDS[kind];
  const Icon = d.icon;
  const retry = kind === 'error' && props.retryable === true && props.onRetry;

  let primary: StateBlockAction | undefined = props.primary;
  let secondary: StateBlockAction | undefined = props.secondary;
  if (kind === 'error') {
    if (retry) primary = { label: 'Try again', onClick: props.onRetry };
    else if (!secondary) secondary = props.docsUrl ? { label: 'What this means', href: props.docsUrl } : undefined;
  }

  const actions: ReactNode[] = [];
  if (primary) actions.push(<ActionButton key="p" action={primary} variant="default" />);
  if (secondary) actions.push(<ActionButton key="s" action={secondary} variant="ghost" />);

  return (
    <div
      className={cn('ifx-state', className)}
      role={kind === 'error' ? 'alert' : 'status'}
      data-kind={kind}
    >
      <span className="ifx-state__icon" data-tone={d.tone} aria-hidden="true"><Icon /></span>
      <div className="ifx-state__title">{props.title ?? d.title}</div>
      <div className="ifx-state__message">{props.message ?? d.message}</div>
      {actions.length > 0 && <div className="ifx-state__actions">{actions}</div>}
      {kind === 'error' && props.requestId && <span className="ifx-state__ref">Reference {props.requestId}</span>}
    </div>
  );
}
